import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { GET_CLASSROOM_MEETINGS } from '../pages/LiveClass/api/LiveClassQuery';
import { ClassroomMeetingsResponse, ClassroomMeetingType, DateRangeValue } from '../utils/interface';
import { toApiRange } from '../utils/attendanceDateRange';
import { FETCH_POLICY } from '../utils/constants';

export const MEETINGS_PAGE_SIZE = 20;

interface UseClassroomMeetingsParams {
  classroomId?: string | null;
  subjectId?: string | null;
  range: DateRangeValue;
  pageSize?: number;
  onError?: (message: string) => void;
}

/**
 * Loads classroom meetings for a date range with offset-based pagination.
 * The first page is fetched whenever the class, subject or range changes; `fetchMore` appends the next page.
 */
export const useClassroomMeetings = ({
  classroomId,
  subjectId,
  range,
  pageSize = MEETINGS_PAGE_SIZE,
  onError,
}: UseClassroomMeetingsParams) => {
  const client = useApolloClient();
  const [meetings, setMeetings] = useState<ClassroomMeetingType[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  // Key of the request whose first page is currently held in state. Loading is derived from it
  // (see below) so there is never a render that shows stale or empty data for a new request.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  // Used to ignore responses of requests that were superseded by a newer range / filter change.
  const requestIdRef = useRef<number>(0);
  const isFetchingMoreRef = useRef<boolean>(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const rangeStartMs = range.start.valueOf();
  const rangeEndMs = range.end.valueOf();
  const hasIds = Boolean(classroomId && subjectId);
  const requestKey = `${classroomId}|${subjectId}|${rangeStartMs}|${rangeEndMs}`;
  const isCurrent = loadedKey === requestKey;
  // True from the very first render of a new request (mount, filter change), not only after an effect runs.
  const loading = hasIds && !isCurrent;

  const fetchPage = useCallback(
    async (offset: number) => {
      const { startTime, endTime } = toApiRange({ start: range.start, end: range.end });
      const { data } = await client.query<ClassroomMeetingsResponse>({
        query: GET_CLASSROOM_MEETINGS,
        variables: { cId: classroomId, sjId: subjectId, startTime, endTime, first: pageSize, offset },
        fetchPolicy: FETCH_POLICY,
      });
      return data?.classroomMeetings;
    },
    // range is tracked through its millisecond values so a new Dayjs instance with the same dates does not refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, classroomId, subjectId, rangeStartMs, rangeEndMs, pageSize],
  );

  // First page: runs on mount and whenever the class, subject or range changes.
  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    isFetchingMoreRef.current = false;
    setLoadingMore(false);
    setMeetings([]);
    setTotalCount(0);
    setHasError(false);

    if (!classroomId || !subjectId) return;

    fetchPage(0)
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        setMeetings(result?.nodes ?? []);
        setTotalCount(result?.totalCount ?? 0);
        setLoadedKey(requestKey);
      })
      .catch((err: Error) => {
        if (requestId !== requestIdRef.current) return;
        setHasError(true);
        setLoadedKey(requestKey);
        onErrorRef.current?.(err?.message);
      });
  }, [fetchPage, classroomId, subjectId, requestKey]);

  const hasMore = isCurrent && meetings.length < totalCount;

  const fetchMore = useCallback(() => {
    if (loading || !hasMore || isFetchingMoreRef.current) return;
    isFetchingMoreRef.current = true;
    setLoadingMore(true);
    const requestId = requestIdRef.current;

    fetchPage(meetings.length)
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        const nextNodes = result?.nodes ?? [];
        // An empty page means the server has nothing more, so stop asking even if totalCount disagrees.
        setTotalCount(nextNodes.length === 0 ? meetings.length : result?.totalCount ?? totalCount);
        setMeetings((prev) => {
          const knownIds = new Set(prev.map((meeting) => meeting.sessionId));
          return [...prev, ...nextNodes.filter((meeting) => !knownIds.has(meeting.sessionId))];
        });
      })
      .catch((err: Error) => {
        if (requestId !== requestIdRef.current) return;
        onErrorRef.current?.(err?.message);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          isFetchingMoreRef.current = false;
          setLoadingMore(false);
        }
      });
  }, [fetchPage, hasMore, loading, meetings.length, totalCount]);

  return {
    meetings: isCurrent ? meetings : [],
    totalCount: isCurrent ? totalCount : 0,
    loading,
    loadingMore,
    hasError: isCurrent && hasError,
    hasMore,
    fetchMore,
  };
};
